/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {ChangeDetectionStrategy, Component} from "@angular/core";
import {Router} from "@angular/router";
import {UserData} from "@app/models/user-data";
import {CommonActions} from "@app/store/common/common.actions";
import {CommonState} from "@app/store/common/common.state";
import {PatientCdDashboardActions} from "@doctor-panel-modules/patient-clinical-dashboard/store/patient-clinical-dashboard.actions";
import {PatientClinicalDashboardState} from "@doctor-panel-modules/patient-clinical-dashboard/store/patient-clinical-dashboard.state";
import {AlertController} from "@ionic/angular";
import {Select, Store} from "@ngxs/store";
import {TrecSitesService} from "@trec/api/utils";
import {C3DarkModeService, C3ThemeColor} from "@trec/components/dark-mode";
import {Observable} from "rxjs";

@Component({
  selector: "app-account-quick-menu",
  templateUrl: "./account-quick-menu.component.html",
  styleUrls: ["./account-quick-menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AccountQuickMenuComponent {
  @Select(CommonState.isAdmin) isAdmin$: Observable<boolean | undefined>;
  @Select(CommonState.isPhysician) isDoctor$: Observable<boolean | undefined>;
  @Select(CommonState.loggedUserData) userData$: Observable<UserData | null | undefined>;
  @Select(PatientClinicalDashboardState.buttonVisible) buttonVisible$: Observable<boolean>;

  readonly isDarkMode$ = this.darkMode.isDarkMode$;

  configSupporto: boolean | null;

  constructor(
    private store: Store,
    private trecSites: TrecSitesService,
    private alertCtrl: AlertController,
    private darkMode: C3DarkModeService,
    private router: Router,
  ) {
    const configuratoreHd = localStorage.getItem("configuratoreHD");
    if (configuratoreHd) {
      this.configSupporto = JSON.parse(configuratoreHd);
    }
  }

  onAdminPanel() {
    this.trecSites.goToAdministrationPage();
  }

  onLeaveFeedback() { }

  onSetThemeColor(theme: C3ThemeColor) {
    this.darkMode.forceTheme(theme);
  }

  goToSupport() {
    return this.router.navigate(["/support"]);
  }

  /**
   * Try to do logout.
  */
  async onLogout() {
    const hasNewData = this.store.selectSnapshot(PatientClinicalDashboardState.buttonVisible);

    if (hasNewData) {
      const alert = await this.alertCtrl.create({
        header: "Dati non visionati",
        message: "Hai nuovi dati che non hai ancora visionato. Vuoi davvero uscire dalla dashboard?",
        buttons: [
          {text: "Rimani", role: "cancel"},
          {
            text: "Esci",
            role: "confirm",
            handler: () => {
              this.store.dispatch(new PatientCdDashboardActions.ResetButtonVisible());
              this.store.dispatch(new CommonActions.Logout());
            }
          }
        ]
      });

      await alert.present();
      return;
    }

    // se non ci sono dati, faccio il logout "normale"
    const alert = await this.alertCtrl.create({
      header: "Logout",
      message: "Vuoi veramente uscire?",
      buttons: [
        {text: "Annulla", role: "cancel"},
        {text: "Logout", handler: () => this.store.dispatch(new CommonActions.Logout())}
      ]
    });

    await alert.present();
  }
}

